import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { RegNumInput } from '../../containers/WP/RegNumInput';
import TopbarSearchForm from '../../containers/TopbarContainer/Topbar/TopbarSearchForm/TopbarSearchForm';
import { trackEvent } from '../../util/analytics';

import css from './SearchField.module.css';

/**
 * SearchField — unified search primitive for the header (and later the hero).
 *
 * Three modes:
 *  - "reg"  — Swedish registration-number lookup (reuses <RegNumInput />,
 *             which handles the API call, series/model disambiguation modal,
 *             and the push into /annonser with filter params).
 *  - "text" — free-text search via the existing <TopbarSearchForm /> so the
 *             category/brand/size autosuggest behavior is preserved verbatim.
 *  - "ai"   — natural-language question (P25009-447). A plain input that
 *             pushes to /sok?fraga=… — the AI answer page owns interpretation
 *             and grounding; nothing model-related lives in the header.
 *
 * Why this layering: <RegNumInput /> alone is ~600 lines of business logic
 * we don't want to duplicate. <TopbarSearchForm /> already has the
 * autosuggest feature the spec asks for in text mode. This component is
 * therefore a thin toggle + host; no lookup logic lives here.
 *
 * Mode persists to localStorage so a user who toggled to "Text" last
 * session doesn't have to toggle again on return.
 *
 * Global "/" hotkey focuses the visible input (ignored while another input
 * is focused — so typing "/" in a textarea or message composer is safe).
 */

const MODE_STORAGE_KEY = 'wheelplace.searchField.mode';

/* Default mode is `text` — the general-purpose autosuggest search is
 * what most users reach for first; REG is a power-user shortcut for
 * the Swedish licence-plate lookup flow. Users who prefer REG or AI can
 * toggle once and their choice persists. */
const readInitialMode = () => {
  if (typeof window === 'undefined') return 'text';
  try {
    const stored = window.localStorage.getItem(MODE_STORAGE_KEY);
    return stored === 'reg' || stored === 'ai' ? stored : 'text';
  } catch {
    return 'text';
  }
};

const SearchField = props => {
  const { size = 'compact', onSearchSubmit, initialSearchFormValues, appConfig } = props;

  /* Lazy useState initializer reads localStorage on the first render
   * directly. The previous `useState('reg')` + `useEffect(() =>
   * setMode(readInitialMode()))` pattern caused a visible one-frame
   * REG→Text flip on every page load — the effect runs after the
   * initial paint, forcing a second render. Reading during init fixes
   * the flash on the client; SSR still falls back to the default. */
  const [mode, setMode] = useState(readInitialMode);
  const [reg, setReg] = useState('');
  const regInputRef = useRef(null);
  const textWrapperRef = useRef(null);
  const aiInputRef = useRef(null);

  const history = useHistory();
  const location = useLocation();

  /* On the AI answer page the header input shows the question being answered,
   * so "change the question" is the same gesture as asking one. Everywhere
   * else it starts empty. */
  const fragaFromUrl =
    location.pathname === '/sok'
      ? new URLSearchParams(location.search).get('fraga') || ''
      : '';
  const [aiQuestion, setAiQuestion] = useState(fragaFromUrl);
  useEffect(() => {
    if (fragaFromUrl) {
      setAiQuestion(fragaFromUrl);
      /* Landing on an AI answer page, the header should show the AI segment
       * with the question in it — but without persisting, so the buyer's
       * stored mode preference survives the visit. */
      setMode('ai');
    }
  }, [fragaFromUrl]);

  const submitAiQuestion = () => {
    const question = aiQuestion.trim();
    if (!question) return;
    trackEvent('header_search_submit', { mode: 'ai' });
    history.push(`/sok?fraga=${encodeURIComponent(question)}`);
  };

  const updateMode = next => {
    setMode(next);
    try {
      window.localStorage.setItem(MODE_STORAGE_KEY, next);
    } catch {
      /* quota or disabled storage — harmless, mode still works for this session */
    }
  };

  /* External components (e.g. WheelFitmentCheck on the listing page)
   * can force the search field into REG mode by dispatching a
   * `wheelplace:switch-search-mode` event. Used by the desktop CTA so
   * a buyer who has the field on Text doesn't end up scrolling to an
   * invisible REG input. */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onSwitch = event => {
      const next = event.detail?.mode;
      if (next === 'reg' || next === 'text' || next === 'ai') updateMode(next);
    };
    window.addEventListener('wheelplace:switch-search-mode', onSwitch);
    return () => window.removeEventListener('wheelplace:switch-search-mode', onSwitch);
  }, []);

  // Keyboard wiring (spec §5.3):
  //   "/"   — focus the visible input, unless the user is already typing
  //           in another field (so "/" in a textarea isn't hijacked).
  //   Esc   — blur the search if it's currently focused.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onKeyDown = event => {
      if (event.key === '/') {
        const target = event.target;
        const tag = target?.tagName?.toLowerCase();
        const isEditable =
          tag === 'input' || tag === 'textarea' || tag === 'select' || target?.isContentEditable;
        if (isEditable) return;
        event.preventDefault();
        const el =
          mode === 'reg'
            ? regInputRef.current
            : mode === 'ai'
            ? aiInputRef.current
            : textWrapperRef.current?.querySelector('input');
        el?.focus();
        return;
      }
      if (event.key === 'Escape') {
        const active = document.activeElement;
        if (!active || active.tagName?.toLowerCase() !== 'input') return;
        const isOurs =
          active === regInputRef.current ||
          active === aiInputRef.current ||
          textWrapperRef.current?.contains(active);
        if (isOurs) active.blur();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mode]);

  const sizeClass = size === 'compact' ? css.compact : css.full;
  const rootClass = `${css.root} ${sizeClass}`;

  return (
    <div className={rootClass}>
      <div className={css.modeToggle} role="tablist" aria-label="Sökläge">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'reg'}
          className={`${css.modePill} ${mode === 'reg' ? css.modePillActive : ''}`}
          onClick={() => updateMode('reg')}
        >
          REG
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'text'}
          className={`${css.modePill} ${mode === 'text' ? css.modePillActive : ''}`}
          onClick={() => updateMode('text')}
        >
          Text
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'ai'}
          className={`${css.modePill} ${mode === 'ai' ? css.modePillActive : ''}`}
          onClick={() => updateMode('ai')}
        >
          AI
        </button>
      </div>

      {/* Both fields stay mounted and stacked on the same slot via
        * absolute positioning; the active one fades/scales in
        * while the inactive one fades/scales out. Gives the mode
        * switch a small brand-polish moment without full
        * mount/unmount flicker. pointer-events + aria-hidden keep
        * the inactive field out of the way for clicks and
        * assistive tech. */}
      <div className={css.inputWrap}>
        <div
          className={`${css.slot} ${mode === 'reg' ? css.slotActive : ''}`}
          aria-hidden={mode !== 'reg'}
        >
          <RegNumInput
            size={size}
            page="searchPage"
            listingType="wheel"
            savedRegNum={reg}
            changeRegNum={setReg}
            inputRef={regInputRef}
          />
        </div>
        <div
          ref={textWrapperRef}
          className={`${css.slot} ${css.textWrap} ${mode === 'text' ? css.slotActive : ''}`}
          aria-hidden={mode !== 'text'}
        >
          <TopbarSearchForm
            onSubmit={values => {
              trackEvent('header_search_submit', { mode: 'text' });
              return onSearchSubmit ? onSearchSubmit(values) : undefined;
            }}
            initialValues={initialSearchFormValues}
            appConfig={appConfig}
          />
        </div>
        <form
          className={`${css.slot} ${css.textWrap} ${mode === 'ai' ? css.slotActive : ''}`}
          aria-hidden={mode !== 'ai'}
          onSubmit={event => {
            event.preventDefault();
            submitAiQuestion();
          }}
        >
          <input
            ref={aiInputRef}
            className={css.aiInput}
            type="text"
            value={aiQuestion}
            onChange={event => setAiQuestion(event.target.value)}
            placeholder="Fråga om vad du behöver…"
            aria-label="AI-sökning"
            maxLength={300}
          />
        </form>
      </div>
    </div>
  );
};

export default SearchField;
